import React, { useState, useCallback } from 'react';
import { X, Mail, ArrowRight, RefreshCw, ExternalLink, Info, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { Checkbox } from '../../molecules/Checkbox';
import './ConnectEmailModal.css';

/**
 * Fixed IMAP port for manually-entered custom-domain hosts. The backend
 * (#1852) hard-rejects any other port and forces implicit TLS server-side,
 * so the wizard shows this read-only rather than offering a port field.
 */
export const MANUAL_IMAP_PORT = 993;

export interface ConnectEmailFormData {
  emailAddress: string;
  name: string;
  password: string;
  /**
   * #1852/#3032: manually-entered IMAP host. Set when the farmer actually chose a
   * host — always for provider === 'unknown', and for a detected provider only when
   * they edited the suggestion. Omitted otherwise, so the server keeps its derived
   * (MX/YAML) host as the default.
   */
  imapHost?: string;
  /** #1852: fixed 993 whenever `imapHost` is set; omitted otherwise. */
  imapPort?: number;
  /**
   * #3035: the IMAP folders the farmer chose to poll. Omitted when the folder tree
   * could not be enumerated, so the server keeps its own default (INBOX + Sent) rather
   * than being handed a guess the client had no basis for.
   */
  folders?: string[];
  /**
   * #3253: how much mailbox history to backfill on connect. Omitted when the farmer
   * left the pre-selected default alone, so the server keeps ownership of what that
   * default means — the same contract as `folders`, and the reason the wizard can show
   * the real behaviour without hard-coding it into every request.
   */
  historicalImport?: HistoricalImportSetting;
}

/**
 * #3253: the five values integration-service accepts (`HISTORICAL_IMPORT_SETTINGS` in
 * `integration-service/src/models/integration.py`). The spellings are load-bearing: an
 * earlier, since-removed UI iteration offered `1_year`/`3_years`, and those now fail the
 * connect with a 400. Anything that maps or prettifies these values on the way to the
 * server reintroduces that break.
 */
export type HistoricalImportSetting = 'none' | 'last_10' | 'last_100' | '1year' | '3years';

/**
 * #3253: what the server does when the request omits `historical_import`. Shown as the
 * pre-selected option so the wizard states the real outcome rather than an empty choice,
 * while an untouched selector still submits nothing (see {@link ConnectEmailFormData}).
 */
export const DEFAULT_HISTORICAL_IMPORT: HistoricalImportSetting = 'last_10';

export interface ProviderDiscoveryResult {
  /** Provider identifier */
  provider: string;
  /** Human-readable provider name */
  providerName: string;
  /**
   * Whether this provider can only be connected by signing in at the provider.
   *
   * #3253: the wizard now **refuses** such a mailbox with an honest notice instead of
   * starting a consent flow. The server still reports `oauth_url` on `/discover`, but
   * nothing in the browser may act on it — the consent branch was a dead end end to end
   * (the generic callback minted an `OAuthConnection`, never an email `Integration`),
   * so the URL is deliberately not modelled here. Reviving it needs the full ship design
   * (state-resolved PKCE, IMAP scopes, tenant keying, #2020 pinning), not a prop.
   */
  requiresOAuth: boolean;
  /** Provider-specific IMAP setup steps (if IMAP must be manually enabled) */
  imapSetupSteps?: string[];
  /** URL to official provider help page for IMAP setup */
  imapSetupHelpUrl?: string;
  /**
   * #1852/#3032: the server's best-effort IMAP host for this address — the MX/YAML
   * derived one for a detected provider, a guess for a custom domain. Prefills the
   * host field but is always editable and never blocks: autodetect guesses are
   * frequently wrong for the live host.
   */
  imapHost?: string;
  /**
   * #3032: whether the farmer may override `imapHost`. True for every password
   * provider (a self-hosted mailbox on an MX-matched domain has no other way to
   * escape the hoster's shared IMAP server), false for OAuth ones. Absent means a
   * pre-#3032 backend, which is treated as the old unknown-provider-only behaviour.
   */
  allowManualOverride?: boolean;
}

/**
 * #3035: which of the three enumeration outcomes the server saw. The distinction drives
 * three different screens, and collapsing it is how a mailbox connects green and ingests
 * nothing:
 *
 * - `resolved` — render the picker.
 * - `empty` — LIST worked and nothing is pollable. Real evidence: block, explain.
 * - `error` — LIST/connect failed. Nothing was learned, so falling back to the server's
 *   default is correct; say so rather than pretending the mailbox has no folders.
 *
 * Absent means a pre-#3035 backend, which is treated exactly like `error`.
 */
export type FolderEnumerationOutcome = 'resolved' | 'empty' | 'error';

/**
 * #3035: mirrors `MAX_POLL_FOLDERS` in `integration-service/src/models/integration.py`, which
 * refuses a larger selection with a 400. Kept here so bulk selection cannot construct a
 * request the server will reject — the per-checkbox path cannot exceed the offered list, and
 * an offered list this long is normal on Gmail (LIST returns every label).
 */
export const MAX_POLL_FOLDERS = 100;

export interface TestConnectionResult {
  success: boolean;
  errorMessage?: string;
  /** #3035: see {@link FolderEnumerationOutcome}. */
  folderEnumeration?: FolderEnumerationOutcome;
  /** #3035: every folder the server offered as a poll target. */
  folders?: string[];
  /**
   * #3035: the server's suggestion — INBOX plus its real Sent folder. Pre-selecting it
   * is what keeps the no-regression promise: a farmer who clicks straight through gets
   * exactly the mailbox behaviour they would have got before #3035.
   */
  suggestedFolders?: string[];
  /**
   * #3184: wire name → RFC 3501 mUTF-7-decoded display name (`INBOX.Grundh&APY-fer 2022`
   * → `INBOX.Grundhöfer 2022`), covering only the entries whose decode differs. Labels
   * only: keys, checked state and the submitted selection stay wire names (decode is not
   * injective). Absent (pre-#3184 backend on a rolling deploy) degrades to raw names.
   */
  displayNames?: Record<string, string>;
}

export interface ConnectEmailModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Handler for closing the modal */
  onClose: () => void;
  /** Handler for form submission (password-based connections only) */
  onConnect: (data: ConnectEmailFormData) => Promise<void>;
  /** Handler for provider discovery - called after email is entered */
  onDiscover?: (email: string) => Promise<ProviderDiscoveryResult | null>;
  /**
   * Handler for testing connection - called after password is entered.
   * #1852: the optional third arg carries a manually-entered IMAP host/port
   * (present only for provider === 'unknown').
   */
  onTestConnection?: (
    email: string,
    password: string,
    imap?: { imapHost?: string; imapPort?: number }
  ) => Promise<TestConnectionResult>;
  /** Optional custom className */
  className?: string;
  /** Labels for i18n support */
  labels?: ConnectEmailModalLabels;
}

export interface ConnectEmailModalLabels {
  title?: string;
  emailAddressLabel?: string;
  emailAddressPlaceholder?: string;
  nameLabel?: string;
  namePlaceholder?: string;
  nameHint?: string;
  passwordLabel?: string;
  back?: string;
  continue?: string;
  connect?: string;
  connecting?: string;
  discovering?: string;
  testingConnection?: string;
  errorEmailRequired?: string;
  errorEmailInvalid?: string;
  errorPasswordRequired?: string;
  errorDiscoveryFailed?: string;
  errorConnectionFailed?: string;
  imapSetupTitle?: string;
  imapSetupSubtitle?: string;
  imapSetupDoneButton?: string;
  imapSetupHelpLinkText?: string;
  showPassword?: string;
  hidePassword?: string;
  /** #1852: banner shown when the provider could not be auto-detected. */
  manualImapBanner?: string;
  /** #3032: hint shown when a provider WAS detected but its host may be overridden. */
  imapHostOverrideHint?: string;
  /** #1852: label for the manual IMAP host input. */
  imapHostLabel?: string;
  /** #1852: placeholder for the manual IMAP host input. */
  imapHostPlaceholder?: string;
  /** #1852: label for the read-only port field. */
  imapPortLabel?: string;
  /** #1852: validation error when the manual IMAP host is empty. */
  errorImapHostRequired?: string;
  /** #3035: heading of the folder-selection step. */
  folderStepTitle?: string;
  /** #3035: explanation of why the farmer is being asked. */
  folderStepSubtitle?: string;
  /** #3035: shown when the tree could not be listed; the server default applies. */
  folderStepEnumerationFailed?: string;
  /** #3035: shown when the mailbox has no pollable folder at all (blocking). */
  folderStepEmpty?: string;
  /** #3035: "{selected} of {total} folders selected". */
  folderStepCoverage?: string;
  /** #3035: select every offered folder. */
  folderSelectAll?: string;
  /** #3035: clear the selection. */
  folderSelectNone?: string;
  /** #3035: validation error when nothing is ticked. */
  errorFolderRequired?: string;
  /** #3253: heading of the historical-import section (inside the folder step). */
  historicalImportTitle?: string;
  /** #3253: explanation of what the backfill will do. */
  historicalImportSubtitle?: string;
  /** #3253: option `none` — connect the mailbox but import nothing already in it. */
  historicalImportNone?: string;
  /** #3253: option `last_10` — the server default. */
  historicalImportLast10?: string;
  /** #3253: option `last_100`. */
  historicalImportLast100?: string;
  /** #3253: option `1year`. */
  historicalImportOneYear?: string;
  /** #3253: option `3years`. */
  historicalImportThreeYears?: string;
  /** #3253: heading of the notice shown when the provider needs a sign-in we cannot finish. */
  oauthUnsupportedTitle?: string;
  /** #3253: body of that notice. `{provider}` is replaced with the discovered provider name. */
  oauthUnsupportedBody?: string;
}

const defaultLabels: Required<ConnectEmailModalLabels> = {
  title: 'Connect Email',
  emailAddressLabel: 'Email Address',
  emailAddressPlaceholder: 'your.email@example.com',
  nameLabel: 'Name (Optional)',
  namePlaceholder: 'Work Email, Personal Email, etc.',
  nameHint: 'This helps you identify this integration later.',
  passwordLabel: 'Email Password',
  back: 'Back',
  continue: 'Continue',
  connect: 'Connect',
  connecting: 'Connecting...',
  discovering: 'Detecting provider...',
  testingConnection: 'Testing connection...',
  errorEmailRequired: 'Please enter your email address.',
  errorEmailInvalid: 'Please enter a valid email address.',
  errorPasswordRequired: 'Please enter your password.',
  errorDiscoveryFailed: 'Could not detect email provider. Please try again.',
  errorConnectionFailed: 'Connection failed. Please check your credentials.',
  imapSetupTitle: 'Enable IMAP Access',
  imapSetupSubtitle: 'Before connecting your mailbox, IMAP access must be enabled in your email provider settings.',
  imapSetupDoneButton: "I've enabled IMAP",
  imapSetupHelpLinkText: 'Official instructions',
  showPassword: 'Show password',
  hidePassword: 'Hide password',
  manualImapBanner: 'Your email provider could not be detected — enter the IMAP server from your hosting provider.',
  imapHostOverrideHint:
    'Detected automatically. Change it only if you run your own mail server for this domain.',
  imapHostLabel: 'IMAP Server',
  imapHostPlaceholder: 'e.g. imap.your-domain.com',
  imapPortLabel: 'Port',
  errorImapHostRequired: 'Please enter your IMAP server.',
  folderStepTitle: 'Which folders should we check?',
  folderStepSubtitle:
    'We only read the folders you select here. If incoming mail is filed into subfolders, select those too.',
  folderStepEnumerationFailed:
    'We could not retrieve the folder list from your mail server. Your inbox and sent folder will be checked; you can adjust this later.',
  folderStepEmpty:
    'We could not find any folder we can check in this mailbox. Please verify the account and try again.',
  folderStepCoverage: '{selected} of {total} folders selected',
  folderSelectAll: 'Select all',
  folderSelectNone: 'Clear',
  errorFolderRequired: 'Please select at least one folder.',
  historicalImportTitle: 'How much history should we import?',
  historicalImportSubtitle:
    'Emails that arrive from now on are always processed. This only decides how far back we look through what is already in the selected folders.',
  historicalImportNone: 'No previous emails',
  historicalImportLast10: 'Only the 10 most recent emails',
  historicalImportLast100: 'The 100 most recent emails',
  historicalImportOneYear: 'The past year',
  historicalImportThreeYears: 'The past three years',
  oauthUnsupportedTitle: 'This mailbox provider is not supported yet',
  oauthUnsupportedBody:
    '{provider} mailboxes can only be connected by signing in at the provider, and we do not support that yet. A mailbox you can connect with an email password works today.',
};

/**
 * #3253: the offered options, in ascending order of how much work the backfill is. The
 * `value`s are the exact server spellings and must not be prettified — see
 * {@link HistoricalImportSetting}.
 */
const HISTORICAL_IMPORT_OPTIONS: ReadonlyArray<{
  value: HistoricalImportSetting;
  labelKey: keyof ConnectEmailModalLabels;
}> = [
  { value: 'none', labelKey: 'historicalImportNone' },
  { value: 'last_10', labelKey: 'historicalImportLast10' },
  { value: 'last_100', labelKey: 'historicalImportLast100' },
  { value: '1year', labelKey: 'historicalImportOneYear' },
  { value: '3years', labelKey: 'historicalImportThreeYears' },
];

/**
 * ConnectEmailModal - Multi-step form for connecting an email account.
 *
 * Wizard flow:
 * 1. Email + name → discover provider
 * 2. (Conditional) IMAP setup instructions for providers that require manual activation
 * 3. Password → test connection (which also enumerates the folder tree)
 * 4. #3035 — folder selection → connect
 *
 * Step 4 is unconditional even when enumeration failed, deliberately. Skipping it on
 * failure would silently apply the INBOX+Sent default — which is the whole defect #3035
 * closes. Rendering it with an explanation instead means the farmer is always told what
 * will be checked.
 */
export const ConnectEmailModal: React.FC<ConnectEmailModalProps> = ({
  isOpen,
  onClose,
  onConnect,
  onDiscover,
  onTestConnection,
  className = '',
  labels = {},
}) => {
  const l = { ...defaultLabels, ...labels };

  const [step, setStep] = useState(1);
  const [emailAddress, setEmailAddress] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  // #3253: discovery reported a provider that can only be connected by signing in at the
  // provider. The wizard stays on step 1 and swaps its fields for a notice, so the refusal
  // costs no step and the progress indicator never changes length.
  const [oauthUnsupported, setOauthUnsupported] = useState(false);
  const [discoveryResult, setDiscoveryResult] = useState<ProviderDiscoveryResult | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [imapHost, setImapHost] = useState('');
  // #3035 — folder selection, populated by the connection test.
  const [folderOptions, setFolderOptions] = useState<string[]>([]);
  const [folderOutcome, setFolderOutcome] = useState<FolderEnumerationOutcome>('error');
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
  // #3184 — wire → decoded labels for the options above. Labels only; all selection
  // state stays on wire names.
  const [folderDisplayNames, setFolderDisplayNames] = useState<Record<string, string>>({});
  // #3253: `undefined` means "the farmer did not touch this", which is what keeps the
  // default's meaning on the server. The radio still renders DEFAULT_HISTORICAL_IMPORT as
  // checked, so the screen shows the real outcome instead of an unanswered question.
  const [historicalImport, setHistoricalImport] = useState<HistoricalImportSetting | undefined>(undefined);
  // Own-property lookup (codex P2): a folder legitimately named `toString`/`__proto__`
  // would otherwise read the INHERITED Object.prototype value from an empty map — truthy,
  // so `?? folder` never fires and the label renders an object/function.
  const folderLabel = (folder: string): string => {
    const value = Object.prototype.hasOwnProperty.call(folderDisplayNames, folder)
      ? folderDisplayNames[folder]
      : undefined;
    return typeof value === 'string' ? value : folder;
  };

  const hasImapSetup = discoveryResult?.imapSetupSteps && discoveryResult.imapSetupSteps.length > 0;
  const imapSetupStep = hasImapSetup ? 2 : -1;
  const passwordStep = hasImapSetup ? 3 : 2;
  // #3035: the folder step always exists, so the progress indicator never changes length
  // mid-wizard (a growing step count reads as a bug).
  const folderStep = passwordStep + 1;
  const totalSteps = folderStep;
  // #1852/#3032: the IMAP host lives on the password step (no extra wizard step;
  // port fixed at 993). Two distinct questions, deliberately kept apart:
  //  - `showHostField`: may the farmer see/edit a host at all? Driven by the
  //    server's `allowManualOverride`, since #3032 true for every password
  //    provider. A discovery result without the flag is a pre-#3032 backend, so it
  //    falls back to the old unknown-provider-only behaviour.
  //  - `isHostRequired`: only when nothing was detected. A detected provider has a
  //    working derived host, so leaving the field untouched must stay valid.
  const isUnknownProvider = discoveryResult?.provider === 'unknown';
  const showHostField = discoveryResult
    ? (discoveryResult.allowManualOverride ?? isUnknownProvider)
    : false;
  const isHostRequired = showHostField && isUnknownProvider;
  // Compare hosts the way the server normalises them (`_normalize_host`: strip, drop a
  // trailing dot, lowercase) so re-typing the suggestion as `SSLIN.DF.EU` or
  // `sslin.df.eu.` is not mistaken for an override — that would cost a needless DNS
  // validation and file the connect as `imap_host_source: "manual"` in telemetry.
  const normaliseHost = (value: string) => value.trim().replace(/\.+$/, '').toLowerCase();
  const suggestedHost = normaliseHost(discoveryResult?.imapHost ?? '');

  const resetState = useCallback(() => {
    setStep(1);
    setEmailAddress('');
    setName('');
    setPassword('');
    setError('');
    setIsConnecting(false);
    setIsDiscovering(false);
    setIsTesting(false);
    setOauthUnsupported(false);
    setDiscoveryResult(null);
    setShowPassword(false);
    setImapHost('');
    setFolderOptions([]);
    setFolderOutcome('error');
    setSelectedFolders([]);
    setFolderDisplayNames({});
    setHistoricalImport(undefined);
  }, []);

  const toggleFolder = useCallback((folder: string) => {
    setError('');
    setSelectedFolders((current) =>
      current.includes(folder) ? current.filter((f) => f !== folder) : [...current, folder]
    );
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const validateStep1 = (): boolean => {
    if (!emailAddress.trim()) {
      setError(l.errorEmailRequired);
      return false;
    }
    if (!emailAddress.includes('@')) {
      setError(l.errorEmailInvalid);
      return false;
    }
    return true;
  };

  const validatePassword = (): boolean => {
    if (!password.trim()) {
      setError(l.errorPasswordRequired);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (step === 1) {
      if (!validateStep1()) {
        return;
      }

      // If discovery callback is provided, check for OAuth requirement
      if (onDiscover) {
        try {
          setIsDiscovering(true);
          const discovery = await onDiscover(emailAddress);

          // #3253: refuse, don't redirect. This branch used to send the farmer to the
          // provider's consent screen, which completed and then produced nothing — the
          // callback minted an OAuthConnection and never an email Integration. The guard
          // keys on `requiresOAuth` ALONE (it used to also require `oauthUrl`): a provider
          // whose config carries no auth_uri is just as unconnectable, and falling through
          // to the password step offers a password the mailbox will not accept.
          if (discovery?.requiresOAuth) {
            setDiscoveryResult(discovery);
            setOauthUnsupported(true);
            setIsDiscovering(false);
            return;
          }

          // Store discovery result for instruction step logic
          setDiscoveryResult(discovery);
          // #1852: prefill the manual host from discovery when available (still
          // editable + never blocking).
          setImapHost(discovery?.imapHost ?? '');
          setIsDiscovering(false);
          setStep(2);
        } catch (err) {
          setIsDiscovering(false);
          setError(err instanceof Error ? err.message : l.errorDiscoveryFailed);
        }
      } else {
        // No discovery callback, proceed directly to password step
        setStep(2);
      }
      return;
    }

    // IMAP setup instruction step - just advance to password
    if (step === imapSetupStep) {
      setStep(passwordStep);
      return;
    }

    if (step === passwordStep) {
      const typedHost = imapHost.trim();

      // #1852: a host is required before we can test/connect an unknown-provider
      // mailbox (there is no derived host, and the backend 400s on an absent one).
      if (isHostRequired && !typedHost) {
        setError(l.errorImapHostRequired);
        return;
      }

      if (!validatePassword()) {
        return;
      }

      // #3032: forward the host only when it is genuinely the farmer's choice —
      // always for an undetected provider, and for a detected one only when they
      // edited the prefilled suggestion. Echoing an untouched suggestion back would
      // turn every ordinary connect into a "manual host" (an extra DNS validation
      // server-side, and a stored host that stops tracking the provider's config).
      const isHostOverridden =
        typedHost !== '' && (isUnknownProvider || normaliseHost(typedHost) !== suggestedHost);
      const manualImap =
        showHostField && isHostOverridden
          ? { imapHost: typedHost, imapPort: MANUAL_IMAP_PORT }
          : undefined;

      // Test connection if callback is provided. Since #3035 a successful test also
      // carries the mailbox's folder tree, which the next step renders.
      if (onTestConnection) {
        try {
          setIsTesting(true);
          const result = await onTestConnection(emailAddress, password, manualImap);
          setIsTesting(false);

          if (!result.success) {
            setError(result.errorMessage || l.errorConnectionFailed);
            return;
          }

          const outcome = result.folderEnumeration ?? 'error';
          const offered = result.folders ?? [];
          setFolderOutcome(outcome);
          setFolderOptions(offered);
          // #3184: absent (pre-#3184 backend) and empty degrade identically to raw names.
          setFolderDisplayNames(result.displayNames ?? {});
          // Default to the server's suggestion (INBOX + its real Sent folder), keeping
          // only names it actually offered — a suggestion outside the offered set could
          // not be unticked and would be invisible on the screen.
          setSelectedFolders((result.suggestedFolders ?? []).filter((f) => offered.includes(f)));
        } catch (err) {
          setIsTesting(false);
          setError(err instanceof Error ? err.message : l.errorConnectionFailed);
          return;
        }
      } else {
        // No test callback (Storybook / a host that skips verification): there is nothing
        // to enumerate from, so the server's default applies and the step says so.
        setFolderOutcome('error');
        setFolderOptions([]);
        setSelectedFolders([]);
        setFolderDisplayNames({});
      }

      setStep(folderStep);
      return;
    }

    if (step === folderStep) {
      // Criterion 5(a): LIST worked and nothing is pollable. Connecting here would create
      // the very mailbox that reports healthy and ingests nothing.
      if (folderOutcome === 'empty') {
        setError(l.folderStepEmpty);
        return;
      }
      if (folderOutcome === 'resolved' && selectedFolders.length === 0) {
        setError(l.errorFolderRequired);
        return;
      }

      const typedHost = imapHost.trim();
      const isHostOverridden =
        typedHost !== '' && (isUnknownProvider || normaliseHost(typedHost) !== suggestedHost);
      const manualImap =
        showHostField && isHostOverridden
          ? { imapHost: typedHost, imapPort: MANUAL_IMAP_PORT }
          : undefined;

      try {
        setIsConnecting(true);
        await onConnect({
          emailAddress,
          name: name.trim() || emailAddress.split('@')[0],
          password,
          ...(manualImap ?? {}),
          // Omitted when enumeration failed: the server then keeps its own default
          // rather than being handed a guess this client had no basis for.
          ...(folderOutcome === 'resolved' ? { folders: selectedFolders } : {}),
          // #3253: same rule for the backfill window — only an explicit choice travels.
          // `undefined` is the sole reason to omit; `'none'` is a choice, not an absence.
          ...(historicalImport !== undefined ? { historicalImport } : {}),
        });
        handleClose();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Connection failed');
        setIsConnecting(false);
      }
    }
  };

  const handleBack = () => {
    setError('');
    // #3253: the refusal notice replaces step 1's fields in place rather than occupying a
    // step of its own, so backing out of it clears the flag. Decrementing the step here
    // would land on step 0 and render an empty wizard.
    if (oauthUnsupported) {
      setOauthUnsupported(false);
      setDiscoveryResult(null);
      return;
    }
    setStep(step - 1);
  };

  if (!isOpen) return null;

  return (
    <div className="connect-email-modal-overlay" onClick={handleClose}>
      <div
        className={`connect-email-modal ${className}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="connect-email-modal-title"
      >
        {/* Header */}
        <div className="connect-email-modal__header">
          <div className="connect-email-modal__header-title">
            <Mail size={20} className="connect-email-modal__header-icon" />
            <h3 id="connect-email-modal-title" className="connect-email-modal__title">
              {l.title}
            </h3>
          </div>
          <button
            type="button"
            className="connect-email-modal__close"
            onClick={handleClose}
            aria-label="Close"
            disabled={isConnecting || isDiscovering}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="connect-email-modal__body">
          {/* Progress Indicator */}
          <div className="connect-email-modal__progress">
            {Array.from({ length: totalSteps }, (_, i) => (
              <React.Fragment key={i}>
                <div
                  className={`connect-email-modal__step ${step === i + 1 ? 'connect-email-modal__step--active' : ''} ${step > i + 1 ? 'connect-email-modal__step--completed' : ''}`}
                >
                  {i + 1}
                </div>
                {i < totalSteps - 1 && <div className="connect-email-modal__step-line" />}
              </React.Fragment>
            ))}
          </div>

          {/* #3253: provider refused — the notice takes step 1's place, never a step of its own */}
          {step === 1 && oauthUnsupported && (
            <div className="connect-email-modal__oauth-unsupported" role="status">
              <div className="connect-email-modal__oauth-unsupported-header">
                <AlertCircle size={20} className="connect-email-modal__oauth-unsupported-icon" />
                <h4 className="connect-email-modal__oauth-unsupported-title">
                  {l.oauthUnsupportedTitle}
                </h4>
              </div>
              <p className="connect-email-modal__oauth-unsupported-body">
                {l.oauthUnsupportedBody.replace(
                  '{provider}',
                  discoveryResult?.providerName || emailAddress
                )}
              </p>
            </div>
          )}

          {/* Step 1: Email Address and Name */}
          {step === 1 && !oauthUnsupported && (
            <>
              <div className="connect-email-modal__field">
                <label htmlFor="email-address" className="connect-email-modal__label">
                  {l.emailAddressLabel}
                </label>
                <input
                  type="email"
                  id="email-address"
                  className="connect-email-modal__input"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  placeholder={l.emailAddressPlaceholder}
                  disabled={isConnecting || isDiscovering}
                  autoFocus
                />
              </div>

              <div className="connect-email-modal__field">
                <label htmlFor="email-name" className="connect-email-modal__label">
                  {l.nameLabel}
                </label>
                <input
                  type="text"
                  id="email-name"
                  className="connect-email-modal__input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={l.namePlaceholder}
                  disabled={isConnecting || isDiscovering}
                />
              </div>
            </>
          )}

          {/* IMAP Setup Instructions Step */}
          {step === imapSetupStep && discoveryResult && (
            <div className="connect-email-modal__imap-setup">
              <div className="connect-email-modal__imap-setup-header">
                <Info size={20} className="connect-email-modal__imap-setup-icon" />
                <h4 className="connect-email-modal__imap-setup-title">{l.imapSetupTitle}</h4>
              </div>
              <p className="connect-email-modal__imap-setup-subtitle">{l.imapSetupSubtitle}</p>
              <ol className="connect-email-modal__imap-setup-steps">
                {discoveryResult.imapSetupSteps!.map((instruction, i) => (
                  <li key={i} className="connect-email-modal__imap-setup-step">
                    {instruction}
                  </li>
                ))}
              </ol>
              {discoveryResult.imapSetupHelpUrl && (
                <a
                  href={discoveryResult.imapSetupHelpUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="connect-email-modal__imap-setup-help-link"
                >
                  <ExternalLink size={14} />
                  {l.imapSetupHelpLinkText}
                </a>
              )}
            </div>
          )}

          {/* Password Step */}
          {step === passwordStep && (
            <>
            {/* #1852/#3032: the IMAP host field, rendered ON the password step (no
                extra wizard step); port is fixed at 993. Shown for every provider
                that allows the override, with copy that matches which case it is:
                "we could not detect you" vs "we detected you, change only if you
                self-host". */}
            {showHostField && (
              <>
                <div className="connect-email-modal__manual-imap-banner" role="note">
                  <Info size={16} className="connect-email-modal__manual-imap-icon" />
                  <span>{isUnknownProvider ? l.manualImapBanner : l.imapHostOverrideHint}</span>
                </div>
                <div className="connect-email-modal__field">
                  <label htmlFor="imap-host" className="connect-email-modal__label">
                    {l.imapHostLabel}
                  </label>
                  <input
                    type="text"
                    id="imap-host"
                    className="connect-email-modal__input"
                    value={imapHost}
                    onChange={(e) => setImapHost(e.target.value)}
                    placeholder={l.imapHostPlaceholder}
                    disabled={isConnecting || isTesting}
                    autoComplete="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    // Take focus only when the host must actually be typed. Since
                    // #3032 this field renders for EVERY password provider, so a bare
                    // autoFocus would drop the common GMX/Vodafone farmer's cursor
                    // into a prefilled technical field instead of the password box.
                    autoFocus={isHostRequired}
                  />
                </div>
                <div className="connect-email-modal__field">
                  <label htmlFor="imap-port" className="connect-email-modal__label">
                    {l.imapPortLabel}
                  </label>
                  <input
                    type="text"
                    id="imap-port"
                    className="connect-email-modal__input connect-email-modal__input--readonly"
                    value={MANUAL_IMAP_PORT}
                    readOnly
                    aria-readonly="true"
                    tabIndex={-1}
                  />
                </div>
              </>
            )}
            <div className="connect-email-modal__field">
              <label htmlFor="email-password" className="connect-email-modal__label">
                {l.passwordLabel}
              </label>
              <div className="connect-email-modal__password-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="email-password"
                  className="connect-email-modal__input connect-email-modal__input--password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isConnecting || isTesting}
                  // Yield focus only when the host above is the field that must be
                  // filled in (an undetected provider); otherwise the password is
                  // still the one thing the farmer has to type.
                  autoFocus={!isHostRequired}
                />
                <button
                  type="button"
                  className="connect-email-modal__password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? l.hidePassword : l.showPassword}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            </>
          )}

          {/* #3035 — Folder Selection Step */}
          {step === folderStep && (
            <div className="connect-email-modal__folders">
              <h4 className="connect-email-modal__folders-title">{l.folderStepTitle}</h4>
              <p className="connect-email-modal__folders-subtitle">{l.folderStepSubtitle}</p>

              {folderOutcome === 'resolved' && (
                <>
                  <div className="connect-email-modal__folders-toolbar">
                    <span className="connect-email-modal__folders-coverage">
                      {l.folderStepCoverage
                        .replace('{selected}', String(selectedFolders.length))
                        .replace('{total}', String(folderOptions.length))}
                    </span>
                    <div className="connect-email-modal__folders-bulk">
                      <button
                        type="button"
                        className="connect-email-modal__folders-bulk-action"
                        onClick={() => {
                          setError('');
                          // Capped: the server refuses a selection above MAX_POLL_FOLDERS
                          // with a 400. A Gmail LIST returns every label, so "more than 100
                          // offerable folders" is ordinary — and one click on Select all
                          // would otherwise walk straight into a rejection the farmer can
                          // neither predict nor read.
                          setSelectedFolders(folderOptions.slice(0, MAX_POLL_FOLDERS));
                        }}
                        disabled={isConnecting}
                      >
                        {l.folderSelectAll}
                      </button>
                      <button
                        type="button"
                        className="connect-email-modal__folders-bulk-action"
                        onClick={() => setSelectedFolders([])}
                        disabled={isConnecting}
                      >
                        {l.folderSelectNone}
                      </button>
                    </div>
                  </div>
                  <ul className="connect-email-modal__folders-list" aria-label={l.folderStepTitle}>
                    {folderOptions.map((folder) => (
                      <li key={folder} className="connect-email-modal__folders-item">
                        <Checkbox
                          checked={selectedFolders.includes(folder)}
                          onChange={() => toggleFolder(folder)}
                          // #3184: decoded label, wire value — key/checked/toggle stay on
                          // the wire name, which is what /connect persists and SELECT needs.
                          label={folderLabel(folder)}
                          ariaLabel={folderLabel(folder)}
                          disabled={isConnecting}
                        />
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {/* Nothing was learned about the tree — say so, and name the fallback. */}
              {folderOutcome === 'error' && (
                <div className="connect-email-modal__manual-imap-banner" role="note">
                  <Info size={16} className="connect-email-modal__manual-imap-icon" />
                  <span>{l.folderStepEnumerationFailed}</span>
                </div>
              )}

              {/* Real evidence that nothing is pollable — blocking, not a warning. */}
              {folderOutcome === 'empty' && (
                <div className="connect-email-modal__folders-empty" role="alert">
                  <AlertCircle size={16} className="connect-email-modal__folders-empty-icon" />
                  <span>{l.folderStepEmpty}</span>
                </div>
              )}

              {/*
                #3253 — Historical-import section. A SECTION, not a step: the wizard's
                length is fixed up front (see `folderStep`), and the same tripwire tests
                that keep #1852's host on the password screen keep this here.

                Hidden only on `empty`, where the connect is blocked anyway — offering a
                backfill window for a mailbox that will not be created is noise. An
                enumeration `error` still shows it: failing to list folders says nothing
                about how far back the farmer wants to read.
              */}
              {folderOutcome !== 'empty' && (
                <fieldset className="connect-email-modal__historical">
                  <legend className="connect-email-modal__historical-title">{l.historicalImportTitle}</legend>
                  <p className="connect-email-modal__historical-subtitle">{l.historicalImportSubtitle}</p>
                  <ul className="connect-email-modal__historical-list">
                    {HISTORICAL_IMPORT_OPTIONS.map(({ value, labelKey }) => {
                      const optionId = `connect-email-modal-historical-${value}`;
                      return (
                        <li key={value} className="connect-email-modal__historical-item">
                          <input
                            type="radio"
                            id={optionId}
                            name="connect-email-modal-historical"
                            className="connect-email-modal__historical-radio"
                            value={value}
                            // An untouched selector reads as the server default, so the
                            // farmer sees what will actually happen — but state stays
                            // `undefined` until they choose, and only a choice is sent.
                            checked={(historicalImport ?? DEFAULT_HISTORICAL_IMPORT) === value}
                            onChange={() => {
                              setError('');
                              setHistoricalImport(value);
                            }}
                            disabled={isConnecting}
                          />
                          <label htmlFor={optionId} className="connect-email-modal__historical-label">
                            {l[labelKey]}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </fieldset>
              )}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="connect-email-modal__error">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="connect-email-modal__actions">
            {step > 1 || oauthUnsupported ? (
              <button
                type="button"
                className="connect-email-modal__back"
                onClick={handleBack}
                disabled={isConnecting || isDiscovering || isTesting}
              >
                {l.back}
              </button>
            ) : (
              <div />
            )}

            {/* #3253: a refused provider has nothing to submit — a live button would only
                re-run discovery to the same refusal. Back is the only way on. */}
            {!oauthUnsupported && (
            <button
              type="submit"
              className="connect-email-modal__submit"
              disabled={isConnecting || isDiscovering || isTesting}
            >
              {isDiscovering ? (
                <>
                  <RefreshCw size={16} className="connect-email-modal__spinner" />
                  {l.discovering}
                </>
              ) : isTesting ? (
                <>
                  <RefreshCw size={16} className="connect-email-modal__spinner" />
                  {l.testingConnection}
                </>
              ) : isConnecting ? (
                <>
                  <RefreshCw size={16} className="connect-email-modal__spinner" />
                  {l.connecting}
                </>
              ) : step === imapSetupStep ? (
                <>
                  {l.imapSetupDoneButton}
                  <ArrowRight size={16} />
                </>
              ) : step === folderStep ? (
                l.connect
              ) : (
                <>
                  {l.continue}
                  <ArrowRight size={16} />
                </>
              )}
            </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConnectEmailModal;
